import { Button, Col, Popconfirm, Row } from "antd";
import Table, { ColumnsType } from "antd/es/table";
import { ColumnType } from "antd/lib/table";
import { useState } from "react";
import Color from "utils/helpers/export-sass-colors";
import { useWidth } from "utils/helpers/use-is-mobile";
import { PlusOutlined } from "@ant-design/icons";
import PermissionsGate from "components/permissionsGate";
import i18next, { t } from "i18next";
import useSystemTerms from "hooks/query/systemTerms/useSystemTerms";
import useDeleteSystemTerm from "hooks/query/systemTerms/useDeleteSystemTerm";
import usePostSystemTerm from "hooks/query/systemTerms/usePostSystemTerm";
import useUpdateSystemTerm from "hooks/query/systemTerms/useUpdateSystemTerm";
import { SystemTermsModel } from "models/systemTerms";
import AddSystemTermModal from "components/Modals/systemTermsModals/AddSystemTermModal";
import UpdateSystemTermModal from "components/Modals/systemTermsModals/UpdateSystemTermModal";

const SystemTerms = () => {
  const { isMobile } = useWidth();
  const { data, isLoading, isFetching } = useSystemTerms();
  const [addOpen, setAddOpen] = useState(false);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [selectedItem, setSelecetedItem] = useState<
    SystemTermsModel | undefined
  >();

  const editHandler = (item: SystemTermsModel) => {
    setSelecetedItem(item);
    setUpdateOpen(true);
  };

  const Delete = useDeleteSystemTerm();

  const add = usePostSystemTerm();
  const update = useUpdateSystemTerm();

  const columns: ColumnsType<SystemTermsModel> = [
    {
      title: t("arabic_name"),
      dataIndex: "name_ar",
      align: "center",
      width: "20%",
    },
    {
      title: t("english_name"),
      dataIndex: "name_en",
      align: "center",
      width: "25%",
    },
    {
      title: t("type"),
      dataIndex: "type",
      align: "center",
      width: "25%",
      render: (type: string) => <span>{t(`${type}_system_terms`)}</span>,
    },
    {
      title: "",
      align: "center",
      width: 200,
      render(value, record) {
        return (
          <div>
            <PermissionsGate scopes={["edit_system_terms"]}>
              <Button type="link" onClick={() => editHandler(record)}>
                {t("update")}
              </Button>
            </PermissionsGate>
            <PermissionsGate scopes={["delete_system_terms"]}>
              <Popconfirm
                okType="danger"
                title={
                  i18next.language === "ar"
                    ? "هل انت متأكد من الحذف؟"
                    : "Are you sure to delete?"
                }
                onConfirm={() => {
                  return new Promise((resolve, reject) => {
                    Delete.mutateAsync(record.id)
                      .then(() => {
                        resolve(null);
                      })
                      .catch(() => {
                        reject();
                      });
                  });
                }}
              >
                <Button danger type="link">
                  {t("delete")}
                </Button>
              </Popconfirm>
            </PermissionsGate>
          </div>
        );
      },
    },
  ];

  const deviceColumns: ColumnType<SystemTermsModel>[] = [
    {
      title: "",
      render: (obj: SystemTermsModel) => {
        return (
          <div
            style={{
              padding: "0",
              borderRadius: "18px",
              overflow: "hidden",
              border: `2px solid ${Color.blue}`,
              width: "90%",
              margin: "auto",
            }}
          >
            <Row style={{ padding: "20px" }}>
              <Col md={12} span={24}>
                <div className="label" style={{ marginBottom: "4px" }}>
                  <span>{t("arabic_name")}: </span>
                  {obj.name_ar}
                </div>
              </Col>
              <Col md={12} span={24}>
                <div className="label" style={{ marginBottom: "4px" }}>
                  <span>{t("english_name")}: </span>
                  {obj.name_en}
                </div>
              </Col>

              <Col md={12} span={24}>
                <div className="label" style={{ marginBottom: "4px" }}>
                  <span>{t("type")}: </span>
                  {t(`${obj.type}_system_terms`)}
                </div>
              </Col>

              <Col md={12} span={24}>
                <PermissionsGate scopes={["edit_categories"]}>
                  <Button type="link" onClick={() => editHandler(obj)}>
                    {t("update")}
                  </Button>
                </PermissionsGate>
                <PermissionsGate scopes={["delete_categories"]}>
                  <Popconfirm
                    okType="danger"
                    title={
                      i18next.language === "ar"
                        ? "هل انت متأكد من الحذف؟"
                        : "Are you sure to delete?"
                    }
                    onConfirm={() => {
                      return new Promise((resolve, reject) => {
                        Delete.mutateAsync({ id: obj.id })
                          .then(() => {
                            resolve(null);
                          })
                          .catch(() => {
                            reject();
                          });
                      });
                    }}
                  >
                    <Button danger type="link">
                      {t("delete")}
                    </Button>
                  </Popconfirm>
                </PermissionsGate>
              </Col>
            </Row>
          </div>
        );
      },
    },
  ];

const addTermHandler = async ({ data }: any) => {
  return add.mutateAsync({ data })
    .then(() => {
      setAddOpen(false);
    })
    .catch((err: any) => {
      console.log("Add Terms failed", err);
    });
};

  const updateTermHandler = async ({ id, data }: any) => {
    return update.mutateAsync({ id, data }).then(() => {
      setUpdateOpen(false);
    })
    .catch((err: any) => {
      console.log("Update Terms failed", err);
    });
  };

  return (
    <div className="categories">
      <PermissionsGate scopes={["add_system_terms"]}>
        <div style={{ textAlign: "end", marginBottom: "1rem" }}>
          <Button
            onClick={() => setAddOpen(true)}
            type="primary"
            icon={<PlusOutlined />}
          >
            {t("add")}
          </Button>
        </div>
      </PermissionsGate>
      <PermissionsGate scopes={["add_system_terms"]}>
        <AddSystemTermModal
          isLoading={add.isLoading}
          open={addOpen}
          setOpen={setAddOpen}
          onAdd={addTermHandler}
        />
      </PermissionsGate>
      <PermissionsGate scopes={["edit_system_terms"]}>
        <UpdateSystemTermModal
          open={updateOpen}
          setOpen={setUpdateOpen}
          item={selectedItem}
          isLoading={update.isLoading}
          onUpdate={updateTermHandler}
        />
      </PermissionsGate>
      <div>
        <Table
          bordered
          columns={isMobile ? deviceColumns : columns}
          dataSource={data?.data}
          loading={isLoading || isFetching}
          pagination={false}
        />
      </div>
    </div>
  );
};

export default SystemTerms;
